import React from 'react';
import {StarIcon as StarIconOutline} from '@heroicons/react/24/outline';
import {StarIcon as StarIconSolid} from '@heroicons/react/24/solid';
import {Link} from 'react-router-dom';
import {PlayerBrowseItem} from '@/features/players/api';

interface PlayerBrowseTableProps {
  players: PlayerBrowseItem[];
  label: string;
  detailHref: (player: PlayerBrowseItem) => string;
  squadStatusById?: ReadonlyMap<string, SquadLifecycleStatus>;
  canManageWatchlist?: boolean;
  watchPending?: ReadonlySet<string>;
  onWatchChange?: (player: PlayerBrowseItem, next: boolean) => void;
}

export type SquadLifecycleStatus = 'review' | 'archived';

export const SquadLifecycleBadge: React.FC<{status?: SquadLifecycleStatus}> = ({status}) => status ? (
  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${status === 'review'
    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
    : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200'}`}>
    {status === 'review' ? 'Review' : 'Archived'}
  </span>
) : null;

export const availabilityLabel = (availability: string): string =>
  `${availability.charAt(0).toUpperCase()}${availability.slice(1)}`;

export const ratingLabel = (player: PlayerBrowseItem): string =>
  player.ratingSource ? String(player.latestRating) : 'Not rated';

export const reportLabel = (count: number): string => `${count} ${count === 1 ? 'report' : 'reports'}`;

export const sourceLabel = (source: string): string => source
  ? `${source.charAt(0).toUpperCase()}${source.slice(1)}`
  : 'No source';

export const reportDateLabel = (value: string | null): string => {
  if (!value) return 'No reports';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getUTCDate()} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};

export const PlayerWatchButton: React.FC<{
  player: PlayerBrowseItem;
  disabled?: boolean;
  onChange: (player: PlayerBrowseItem, next: boolean) => void;
}> = ({player, disabled, onChange}) => {
  const label = player.isWatched
    ? `Remove ${player.name} from watching`
    : `Add ${player.name} to watching`;
  const Icon = player.isWatched ? StarIconSolid : StarIconOutline;
  return (
    <button
      type="button"
      className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-wait disabled:opacity-60 ${player.isWatched
        ? 'text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300'
        : 'text-gray-500 hover:text-amber-500 dark:text-gray-400 dark:hover:text-amber-400'}`}
      aria-label={label}
      aria-pressed={player.isWatched}
      disabled={disabled}
      onClick={() => onChange(player, !player.isWatched)}
    >
      <Icon className="h-6 w-6" aria-hidden="true" />
    </button>
  );
};

const PlayerBrowseTable: React.FC<PlayerBrowseTableProps> = ({
  players,
  label,
  detailHref,
  squadStatusById,
  canManageWatchlist,
  watchPending,
  onWatchChange,
}) => (
  <table aria-label={label} className="w-full table-auto border-separate border-spacing-0 rounded-xl bg-white text-left shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
    <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
      <tr>
        <th scope="col" className="px-4 py-3">Player</th>
        <th scope="col" className="px-4 py-3">Position</th>
        <th scope="col" className="px-4 py-3">Team</th>
        <th scope="col" className="px-4 py-3">Age</th>
        <th scope="col" className="px-4 py-3">Availability</th>
        <th scope="col" className="px-4 py-3">Latest rating</th>
        <th scope="col" className="px-4 py-3">Rating source</th>
        <th scope="col" className="px-4 py-3">Last report</th>
        <th scope="col" className="px-4 py-3">Reports</th>
        {canManageWatchlist && <th scope="col" className="px-2 py-3"><span className="sr-only">Watching</span></th>}
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-200 text-sm text-gray-700 dark:divide-gray-800 dark:text-gray-200">
      {players.map(player => (
        <tr key={player.id}>
          <th scope="row" className="px-4 py-4 font-semibold text-gray-900 dark:text-gray-100">
            <span className="flex min-w-0 items-center gap-3">
              {player.photoUrl && <img className="h-10 w-10 shrink-0 rounded-full object-cover" src={player.photoUrl} alt={player.name} />}
              <Link className="break-words rounded-md text-club-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-club-blue-300" to={detailHref(player)}>
                {player.name}
              </Link>
              <SquadLifecycleBadge status={squadStatusById?.get(player.id)} />
            </span>
          </th>
          <td className="px-4 py-4">{player.position}</td>
          <td className="px-4 py-4">{player.team ?? 'No team'}</td>
          <td className="px-4 py-4">{player.age}</td>
          <td className="px-4 py-4">{availabilityLabel(player.availability)}</td>
          <td className="px-4 py-4">{ratingLabel(player)}</td>
          <td className="px-4 py-4">{sourceLabel(player.ratingSource)}</td>
          <td className="whitespace-nowrap px-4 py-4"><time dateTime={player.lastReportAt ?? undefined}>{reportDateLabel(player.lastReportAt)}</time></td>
          <td className="px-4 py-4">{reportLabel(player.reportCount)}</td>
          {canManageWatchlist && (
            <td className="px-2 py-2 text-right">
              {onWatchChange && player.scope === 'external' && (
                <PlayerWatchButton player={player} disabled={watchPending?.has(player.id)} onChange={onWatchChange} />
              )}
            </td>
          )}
        </tr>
      ))}
    </tbody>
  </table>
);

export default PlayerBrowseTable;
